import React, { FC, useEffect, useState } from "react";
import { Drawer, Snackbar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SnackbarContent from "@mui/material/SnackbarContent";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import CategoryOutlined from "@mui/icons-material/CategoryOutlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import { GoPersonFill } from "react-icons/go";
import { jwtDecode } from "jwt-decode";

import { useLanguage } from "../LanguageContext";
import { userDrawerTranslations } from "../userDrawer/userDrawerTranslations";
import { Language } from "../../Types/types";

interface AdminDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface TokenPayload {
  iat: number;
  exp: number;
  roles: string[];
  username: string;
  clientIdentifier: string;
}

const AdminDrawer: FC<AdminDrawerProps> = ({ open, onClose }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [username, setUsername] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const t = userDrawerTranslations[language as Language];
  const copyLabel = (t as any).copyUsername || "Copy username";
  const navLabel = (t as any).navigationLabel || "Navigation";

  useEffect(() => {
    if (open) {
      const storedUsername = localStorage.getItem("username");
      const token = localStorage.getItem("token");

      if (storedUsername) {
        setUsername(storedUsername);
      }

      if (token) {
        try {
          const decodedToken = jwtDecode<TokenPayload>(token);
          setIsAdmin(decodedToken.roles.includes("ROLE_ADMIN"));
        } catch (error) {
          console.error("Invalid token", error);
        }
      }
    }
  }, [open]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
    onClose();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(username);
    setSnackbarOpen(true);
  };

  const navigateTo = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isSmallScreen ? "100%" : 380,
          minWidth: 320,
          borderTopRightRadius: 16,
          borderBottomRightRadius: 16,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(88, 110, 223, .8)",
        },
      }}
    >
      <div className="bg-white dark:bg-darkSecondaryBackground h-screen flex flex-col">
        <div className="p-4 flex flex-col h-full">
          <div className="flex justify-end p-2">
            <button
              onClick={onClose}
              aria-label="Close menu"
              className="rounded-full p-2 hover:bg-black/5 dark:hover:bg-white/10 text-black dark:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="flex flex-col h-full">
            {/* Username card */}
            <div className="flex items-center gap-3 bg-lightPurple dark:bg-darkBackground/60 border border-purple/40 dark:border-purple/50 rounded-lg px-3 py-2 shadow-sm">
              <div className="text-black dark:text-white">
                <GoPersonFill style={{ fontSize: "1.25rem" }} />
              </div>
              <span className="text-sm font-medium text-black dark:text-white truncate">
                {username || t.usernamePlaceholder}
              </span>
              <button
                onClick={handleCopy}
                aria-label={copyLabel}
                className="ml-auto inline-flex items-center justify-center rounded-md p-2 hover:bg-black/5 dark:hover:bg-white/10 text-black dark:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
              >
                <ContentCopyIcon
                  fontSize="small"
                  className="text-black dark:text-white"
                />
              </button>
            </div>

            {/* Navigation */}
            <div className="mt-6">
              <p className="px-2 text-xs uppercase tracking-wider text-gray-500 dark:text-white mb-2">
                {navLabel}
              </p>
              <nav className="flex flex-col gap-2">
                <button
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-800 dark:text-white bg-white shadow-sm dark:bg-darkBackground/60 dark:border dark:border-purple/30 hover:shadow-md dark:hover:bg-darkBackground/70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
                  onClick={() => navigateTo("/packages-list")}
                >
                  <Inventory2Outlined
                    fontSize="small"
                    className="text-gray-600 dark:text-white"
                  />
                  <span className="font-medium">{t.packages}</span>
                  <ChevronRight
                    className="ml-auto text-gray-400 dark:text-white/70"
                    fontSize="small"
                  />
                </button>
                <button
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-800 dark:text-white bg-white shadow-sm dark:bg-darkBackground/60 dark:border dark:border-purple/30 hover:shadow-md dark:hover:bg-darkBackground/70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
                  onClick={() => navigateTo("/categories-list")}
                >
                  <CategoryOutlined
                    fontSize="small"
                    className="text-gray-600 dark:text-white"
                  />
                  <span className="font-medium">{t.categories}</span>
                  <ChevronRight
                    className="ml-auto text-gray-400 dark:text-white/70"
                    fontSize="small"
                  />
                </button>
                <button
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-800 dark:text-white bg-white shadow-sm dark:bg-darkBackground/60 dark:border dark:border-purple/30 hover:shadow-md dark:hover:bg-darkBackground/70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
                  onClick={() => navigateTo("/licenses-list")}
                >
                  <VerifiedUserOutlined
                    fontSize="small"
                    className="text-gray-600 dark:text-white"
                  />
                  <span className="font-medium">{t.licenses}</span>
                  <ChevronRight
                    className="ml-auto text-gray-400 dark:text-white/70"
                    fontSize="small"
                  />
                </button>
              </nav>
            </div>

            {/* Footer actions */}
            <div className="flex flex-col justify-end mt-auto gap-2">
              {isAdmin && (
                <button
                  onClick={() => navigateTo("/")}
                  className="bg-lightPurple text-black dark:text-white dark:bg-darkBackground/60 ring-1 ring-purple/40 dark:ring-purple/40 hover:bg-lightPurple/90 dark:hover:bg-darkBackground/70 font-semibold w-full rounded-lg px-4 py-2"
                >
                  {t.userZoneButton}
                </button>
              )}
              <button
                onClick={handleLogout}
                className="bg-purple hover:bg-darkPurple text-white w-full px-4 py-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
              >
                {t.logoutButton}
              </button>
            </div>
          </div>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <SnackbarContent
            message={t.usernameCopied}
            sx={{ backgroundColor: "orange", color: "white" }}
          />
        </Snackbar>
      </div>
    </Drawer>
  );
};

export default AdminDrawer;
